import React, { useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronUp, Play } from 'lucide-react';

export interface TestCase {
  id?: string;
  stdin: string;
  stdout: string;
  scoreWeight: number;
  isHidden?: boolean;
  sortOrder?: number;
}

interface TestCaseManagerProps {
  testCases: TestCase[];
  onChange: (testCases: TestCase[]) => void;
  onRunTest?: (code: string, testCases: TestCase[]) => Promise<void>;
  referenceCode?: string;
}

const TestCaseManager: React.FC<TestCaseManagerProps> = ({
  testCases,
  onChange,
  onRunTest,
  referenceCode
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const addTestCase = () => {
    const newCase: TestCase = {
      id: Date.now().toString(),
      stdin: '',
      stdout: '',
      scoreWeight: 1,
      isHidden: false,
      sortOrder: testCases.length
    };
    onChange([...testCases, newCase]);
  };

  const removeTestCase = (index: number) => {
    const updated = testCases.filter((_, i) => i !== index);
    onChange(updated.map((tc, i) => ({ ...tc, sortOrder: i })));
  };

  const updateTestCase = (index: number, field: keyof TestCase, value: any) => {
    const updated = testCases.map((tc, i) =>
      i === index ? { ...tc, [field]: value } : tc
    );
    onChange(updated);
  };

  const handleRunTest = async () => {
    if (!onRunTest || !referenceCode) return;
    setIsRunning(true);
    try {
      await onRunTest(referenceCode, testCases);
    } catch (error) {
      console.error('运行测试失败:', error);
      alert('运行测试失败，请检查代码和用例');
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white">
      {/* 折叠头部 */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">测试用例管理</span>
          <span className="text-xs text-gray-500">({testCases.length}组)</span>
        </div>
        <div className="flex items-center gap-2">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* 展开内容 */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-3">
          {/* 操作按钮 */}
          <div className="flex items-center gap-2">
            <button
              onClick={addTestCase}
              className="flex items-center gap-1 px-3 py-1.5 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
            >
              <Plus className="w-3 h-3" />添加用例
            </button>
            {onRunTest && referenceCode && (
              <button
                onClick={handleRunTest}
                disabled={isRunning || testCases.length === 0}
                className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-xs rounded hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <Play className="w-3 h-3" />
                {isRunning ? '运行中...' : '运行测试'}
              </button>
            )}
          </div>

          {/* 用例列表 */}
          {testCases.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              暂无测试用例，点击"添加用例"开始配置
            </div>
          ) : (
            <div className="space-y-3">
              {testCases.map((tc, index) => (
                <div key={tc.id || index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-600">用例 {index + 1}</span>
                    <button
                      onClick={() => removeTestCase(index)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                      title="删除用例"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">标准输入 (stdin)</label>
                      <textarea
                        value={tc.stdin}
                        onChange={(e) => updateTestCase(index, 'stdin', e.target.value)}
                        placeholder="请输入测试输入..."
                        className="w-full h-20 px-2 py-1.5 border border-gray-200 rounded text-xs font-mono resize-none focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">期望输出 (stdout)</label>
                      <textarea
                        value={tc.stdout}
                        onChange={(e) => updateTestCase(index, 'stdout', e.target.value)}
                        placeholder="请输入期望输出..."
                        className="w-full h-20 px-2 py-1.5 border border-gray-200 rounded text-xs font-mono resize-none focus:border-blue-500 focus:outline-none bg-white"
                      />
                    </div>
                  </div>

                  <div className="mt-2 flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500">分值权重:</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={tc.scoreWeight}
                        onChange={(e) => updateTestCase(index, 'scoreWeight', parseInt(e.target.value) || 1)}
                        className="w-16 px-2 py-1 border border-gray-200 rounded text-xs focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={tc.isHidden || false}
                        onChange={(e) => updateTestCase(index, 'isHidden', e.target.checked)}
                        className="w-3 h-3"
                      />
                      <span className="text-xs text-gray-500">隐藏用例（不向学生展示）</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TestCaseManager;
